import { useState, useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";
import {
  buildAiSubCategorySelections,
  type CreateCalculationFormData,
} from "../components/CreateCalculationPanel/CreateCalculationPanel.constants";
import useFileUpload, { type UploadedFile } from "./useFileUpload";
import useAiDraft, { type AiPhase } from "./useAiDraft";

/** Note character cap (mirrors backend MAX_MESSAGE_LENGTH). Single source of truth —
 *  the composer imports it for its counter/limit copy, rather than re-declaring the number. */
export const maxNoteLength = 2000;

export interface AiDraftController {
  phase: AiPhase;
  files: UploadedFile[];
  note: string;
  setNote: (value: string) => void;
  /** Resolves to a message when the file cap was hit (extra files dropped), else null. */
  addFiles: (files: File[]) => Promise<string | null>;
  removeFile: (id: string) => void;
  canSubmit: boolean;
  submit: () => void;
  retry: () => void;
}

/**
 * Owns the AI draft flow's state (upload + note + job polling) and derives the
 * submit gate. Consumed by `useAiChat`, which adapts it into the chat surface's
 * message thread + composer.
 */
const useAiDraftController = (
  form: UseFormReturn<CreateCalculationFormData>,
): AiDraftController => {
  const [note, setNote] = useState("");
  const { files, admittedFileIds, addFiles, removeFile } = useFileUpload();
  const { phase, start, retry } = useAiDraft();

  const noteOverLimit = note.length > maxNoteLength;
  const canSubmit =
    !noteOverLimit && (admittedFileIds.length > 0 || note.trim() !== "");

  const submit = useCallback(() => {
    const data = form.getValues();
    if (!canSubmit || data.subCategoryIds.length === 0) return;
    void start(
      {
        supplierId: data.supplierId,
        name: data.name,
        line: data.line,
        planningYear: data.planningYear,
        kamName: data.kamName,
        subCategorySelections: buildAiSubCategorySelections(data),
      },
      admittedFileIds,
      note.trim() || null,
    );
  }, [canSubmit, form, start, admittedFileIds, note]);

  return {
    phase,
    files,
    note,
    setNote,
    addFiles,
    removeFile,
    canSubmit,
    submit,
    retry,
  };
};

export default useAiDraftController;
